// @ts-expect-error Could not find a declaration file for module 'bidi-js'
import createBidi from "bidi-js";
import { fabric } from "fabric";
import { jsPDF as JsPDF } from "jspdf";
import normalizeUrl from "normalize-url";

import { getNumberOfPages } from "../../canvas/page-panning";
import { resolveFont } from "../../fonts/font-registry";
import { pixelsToPoints } from "../paper.utils";
import type { CanvasPaperConfig } from "./PageDesigner";
import { sanitizeSvgImagesForPdf } from "./pdf-document.helpers";

/**
 * Creates a PDF document from the given fabric canvas and configuration.
 *
 * @param canvas - The fabric canvas to be converted to a PDF. It contains various objects
 *                 which may include text, images, and shapes.
 * @param canvasPaperConfig - Configuration object containing paper size and orientation.
 *                            This defines the dimensions and layout of the PDF pages.
 * @param callback - Function to be called after the PDF document is created. This allows
 *                   further processing of the PDF document, such as saving or downloading it.
 */
export const createPdfDocument = async (
  canvas: fabric.Canvas,
  canvasPaperConfig: CanvasPaperConfig,
  callback: (doc: JsPDF) => void,
): Promise<void> => {
  const pageWidthInPoints = pixelsToPoints(canvasPaperConfig.width);
  const pageHeightInPoints = pixelsToPoints(canvasPaperConfig.height);

  const svgElement = canvasToSVG(canvas, canvasPaperConfig);

  if (svgElement) {
    sanitizeSvgImagesForPdf(svgElement);
  }

  const pdf = new JsPDF({
    orientation: canvasPaperConfig.orientation,
    unit: "pt",
    format: [pageWidthInPoints, pageHeightInPoints],
  });

  if (svgElement) {
    await addFontsToPDF(pdf, svgElement);
    await svgToPDF(pdf, svgElement, pageWidthInPoints, pageHeightInPoints);
  }

  // Links must be added after svgToPDF so every page already exists — each
  // annotation is attached to the page its element is rendered on.
  addStaticLinksToPdf(pdf, canvas, pageHeightInPoints);

  callback(pdf);
};

/**
 * Adds static links to the PDF document if they exist in the canvas objects.
 *
 * @param doc - The JsPDF document to which links will be added. This document will contain
 *              clickable areas that redirect to specified URLs when viewed or printed.
 * @param canvas - The fabric canvas containing objects with potential URLs. Each object is
 *                 checked for a URL, and if found, the link is created accordingly.
 */
const addStaticLinksToPdf = (
  doc: JsPDF,
  canvas: fabric.Canvas,
  pageHeightInPoints: number,
): void => {
  const links: StaticLink[] = [];
  collectStaticLinks(canvas._objects, 0, 0, links);

  for (const { left, top, width, height, url } of links) {
    // The svg is sliced into pages by vertical offset; attach the annotation
    // to the page the element lands on, with the top relative to that page.
    const topInPoints = pixelsToPoints(top);
    const pageIndex = Math.min(
      Math.max(Math.floor(topInPoints / pageHeightInPoints), 0),
      doc.getNumberOfPages() - 1,
    );
    doc.setPage(pageIndex + 1);

    doc.link(
      pixelsToPoints(left),
      topInPoints - pageIndex * pageHeightInPoints,
      pixelsToPoints(width),
      pixelsToPoints(height),
      {
        url: safeNormalizeUrl(url),
      },
    );
  }
};

interface StaticLink {
  left: number;
  top: number;
  width: number;
  height: number;
  url: string;
}

/**
 * Walks the object tree and collects every object carrying `data.url`,
 * resolving coordinates to canvas-absolute values. The url may live on a
 * top-level object (e.g. address maps) or on objects nested inside groups —
 * the textbox of a BorderedTextBox (text/link/field elements) or the cells
 * of a linked-record Table.
 */
const collectStaticLinks = (
  objects: fabric.Object[],
  offsetLeft: number,
  offsetTop: number,
  links: StaticLink[],
): void => {
  for (const obj of objects) {
    if (obj.visible === false) {
      continue;
    }

    let left = offsetLeft + (obj.left ?? 0);
    const top = offsetTop + (obj.top ?? 0);

    const url: string | undefined = obj.data?.url;
    const urls: string[] = Array.isArray(obj.data?.urls) ? obj.data.urls : [];

    const isMultiUrlText =
      obj instanceof fabric.Text &&
      urls.length > 1 &&
      collectTextLineLinks(obj, left, top, links);

    const singleUrl = url ?? (urls.length === 1 ? urls[0] : undefined);
    if (!isMultiUrlText && singleUrl) {
      let { width = 0, height = 0 } = obj;
      if (obj instanceof fabric.Text) {
        // Cover the rendered text only — every wrapped line, not just the box.
        const lineWidths = obj.__lineWidths ?? [];
        const textWidth = lineWidths.length ? Math.max(...lineWidths) : 0;
        if (textWidth && textWidth < width) {
          if (obj.textAlign === "right") {
            left += width - textWidth;
          } else if (obj.textAlign === "center") {
            left += (width - textWidth) / 2;
          }
          width = textWidth;
        }
        height = obj.calcTextHeight() || height;
      }

      links.push({ left, top, width, height, url: singleUrl });
    }

    if (obj instanceof fabric.Group) {
      // Children coordinates are relative to the group's center.
      collectStaticLinks(
        obj.getObjects(),
        left + (obj.width ?? 0) / 2,
        top + (obj.height ?? 0) / 2,
        links,
      );
    }
  }
};

/**
 * Annotates a text block whose value holds several urls (a multi-value link
 * field). The rendered lines are segmented at each line that starts a new
 * url, and every segment gets its own annotation with the corresponding full
 * url. Returns false when the segments cannot be matched to the urls — the
 * caller then falls back to a single-url annotation.
 */
const collectTextLineLinks = (
  obj: fabric.Text,
  left: number,
  top: number,
  links: StaticLink[],
): boolean => {
  const urls: string[] = obj.data?.urls ?? [];
  const lines = (obj._textLines ?? []).map((graphemes) => graphemes.join(""));

  const segmentStarts = lines.reduce<number[]>((acc, line, index) => {
    if (/^https?:\/\//.test(line)) {
      acc.push(index);
    }
    return acc;
  }, []);

  if (segmentStarts.length !== urls.length) {
    return false;
  }

  const lineWidths = obj.__lineWidths ?? [];
  const boxWidth = obj.width ?? 0;

  const lineTops: number[] = [];
  let accumulatedTop = 0;
  lines.forEach((_, index) => {
    lineTops.push(accumulatedTop);
    accumulatedTop += obj.getHeightOfLine(index);
  });

  segmentStarts.forEach((startLine, segmentIndex) => {
    const endLine =
      segmentIndex + 1 < segmentStarts.length
        ? segmentStarts[segmentIndex + 1]
        : lines.length;

    let height = 0;
    let textWidth = 0;
    for (let index = startLine; index < endLine; index++) {
      height += obj.getHeightOfLine(index);
      textWidth = Math.max(textWidth, lineWidths[index] ?? 0);
    }

    let segmentLeft = left;
    let width = boxWidth;
    if (textWidth && textWidth < boxWidth) {
      if (obj.textAlign === "right") {
        segmentLeft += boxWidth - textWidth;
      } else if (obj.textAlign === "center") {
        segmentLeft += (boxWidth - textWidth) / 2;
      }
      width = textWidth;
    }

    links.push({
      left: segmentLeft,
      top: top + lineTops[startLine],
      width,
      height,
      url: urls[segmentIndex],
    });
  });

  return true;
};

const safeNormalizeUrl = (url: string): string => {
  // normalize-url throws on values it cannot parse; a malformed user-entered
  // url must not abort the whole PDF export.
  try {
    return normalizeUrl(url);
  } catch {
    return url;
  }
};

/**
 * Adds fonts used in the SVG to the PDF document.
 *
 * @param doc - The JsPDF document where fonts will be added. This document requires fonts
 *              to render text correctly in the PDF output.
 * @param svgElement - The SVG element from which font faces will be extracted. The function
 *                     checks for all used font families and styles to ensure they are added
 *                     to the PDF document.
 */
export const addFontsToPDF = async (
  doc: JsPDF,
  svgElement: SVGElement,
): Promise<void> => {
  await Promise.allSettled(
    getListOfUsedFontFaces(svgElement).map(
      async ([fontFamily, fontStyle, fontWeight]) => {
        const fontId = `${fontFamily}_${fontStyle}_${fontWeight}`.replace(
          /\s/g,
          "",
        );
        // Resolve via the shared registry so the embedded font is the same file
        // the browser preview measured with. fontId stays the VFS key and
        // addFont keeps the family name svg2pdf matches against.
        const fontContent = await resolveFont(fontFamily, fontWeight).load();
        doc.addFileToVFS(fontId, fontContent);
        doc.addFont(fontId, fontFamily, fontStyle, fontWeight);
      },
    ),
  );
};

/**
 * Converts the fabric canvas to an SVG element.
 *
 * @param canvas - The fabric canvas to be converted. It contains various objects that will
 *                 be represented in the SVG format.
 * @param canvasPaperConfig - Configuration object containing canvas dimensions. This is used
 *                            to set the viewBox of the generated SVG.
 * @returns An SVGElement created from the canvas or null if conversion fails.
 */
/* istanbul ignore next */
const canvasToSVG = (
  canvas: fabric.Canvas,
  canvasPaperConfig: CanvasPaperConfig,
): SVGElement | null => {
  const totalHeight =
    canvasPaperConfig.height * getNumberOfPages(canvas, canvasPaperConfig);

  const svg = canvas.toSVG({
    suppressPreamble: true,
    // set svg element size to avoid a blank page
    width: canvasPaperConfig.width,
    height: totalHeight,
    viewBox: {
      x: 0,
      y: 0,
      width: canvasPaperConfig.width,
      height: totalHeight,
    },
  });

  const parser = new DOMParser();
  const doc = parser.parseFromString(svg, "image/svg+xml");

  const textElements = doc.querySelectorAll("text, tspan");
  textElements.forEach((el) => {
    el.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE && node.textContent) {
        node.textContent = convertHebrew(node.textContent);
      }
    });
  });

  return doc.firstElementChild as SVGElement;
};

/**
 * Renders the given SVG element to the PDF document.
 *
 * @param doc - The JsPDF document to render the SVG into. This document will contain the
 *              visual representation of the SVG element once rendering is complete.
 * @param svgElement - The SVG element to be rendered. It should represent the full content
 *                     of the canvas in SVG format.
 * @param pageWidthInPoints - The width of the page in points. This is used to set the width
 *                            of the rendered SVG in the PDF.
 * @param pageHeightInPoints - The height of the page in points. This is used to determine
 *                             if the SVG content exceeds the page size, necessitating more pages.
 */
const svgToPDF = async (
  doc: JsPDF,
  svgElement: SVGElement,
  pageWidthInPoints: number,
  pageHeightInPoints: number,
): Promise<void> => {
  const svgHeight = Number(svgElement?.getAttribute("height"));
  const svgHeightInPoints = pixelsToPoints(svgHeight);

  await doc.svg(svgElement, {
    x: 0,
    y: 0,
    width: pageWidthInPoints,
    height: svgHeightInPoints,
  });

  if (svgHeightInPoints > pageHeightInPoints) {
    const pages = Math.ceil(svgHeightInPoints / pageHeightInPoints);
    for (let i = 0; i < pages - 1; i++) {
      doc.addPage([pageWidthInPoints, pageHeightInPoints]);
      await doc.svg(svgElement, {
        x: 0,
        y: -pageHeightInPoints * (i + 1),
        width: pageWidthInPoints,
        height: svgHeightInPoints,
      });
    }
  }
};

/**
 * Retrieves a list of unique font faces used within a given SVG element.
 *
 * Each font face is represented as a tuple containing the font family,
 * font style, and font weight.
 *
 * @param element - The SVG element to inspect for font faces.
 * @returns An array of tuples, where each tuple contains the font family, style, and weight.
 */
const getListOfUsedFontFaces = (
  element: Element,
): Array<[string, string, string]> => {
  const fontFacesSet = new Set<string>();

  element?.querySelectorAll("*").forEach((e) => {
    const fontFamily = e.getAttribute("font-family");
    const fontStyle = e.getAttribute("font-style");
    const fontWeight = e.getAttribute("font-weight");

    if (fontFamily && fontStyle) {
      const fontFaceKey = fontWeight
        ? `${fontFamily}__${fontStyle}__${fontWeight}`
        : `${fontFamily}__${fontStyle}`;
      fontFacesSet.add(fontFaceKey);
    }
  });

  return [...fontFacesSet]
    .map<[string, string, string]>((fontFace: string) => {
      const [family, style, weight] = fontFace.split("__");
      return [family || "", style || "", weight || ""];
    })
    .filter(([family, style]) => family && style);
};

export function convertHebrew(text: string): string {
  if (!text) return text;

  // Check for the presence of Hebrew characters
  const isHebrew = /[\u0590-\u05FF]/.test(text);
  if (!isHebrew) {
    return text;
  }

  const bidi = createBidi();

  const embeddingLevelsResult = bidi.getEmbeddingLevels(text, "ltr");
  const { levels } = embeddingLevelsResult;

  // Проверяем есть ли RTL вообще (нечётный уровень = RTL)
  let hasRtl = false;
  for (let i = 0; i < levels.length; i++) {
    if (levels[i] % 2 === 1) {
      hasRtl = true;
      break;
    }
  }

  if (!hasRtl) {
    return text;
  }

  const reorderedIndices = bidi.getReorderedIndices(
    text,
    embeddingLevelsResult,
  );

  // Собираем результат
  const chars: string[] = [];
  const newLevels: number[] = [];

  for (let i = 0; i < reorderedIndices.length; i++) {
    const origIdx = reorderedIndices[i];
    chars.push(text[origIdx]);
    newLevels.push(levels[origIdx]);
  }

  // Реверсим LTR сегменты обратно (чётный уровень = LTR)
  let i = 0;
  while (i < chars.length) {
    if (newLevels[i] % 2 === 0) {
      const start = i;
      while (i < chars.length && newLevels[i] % 2 === 0) {
        i++;
      }
      // Реверсим этот LTR сегмент
      let left = start;
      let right = i - 1;
      while (left < right) {
        const tmp = chars[left];
        chars[left] = chars[right];
        chars[right] = tmp;
        left++;
        right--;
      }
    } else {
      i++;
    }
  }

  return chars.join("");
}
